import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  AssignmentOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  VisibilityOutlined,
  SearchOutlined,
  CurrencyRupeeOutlined,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import backgroundPattern from "../../assets/pasbookpng.png";
import "../Invoices/Invoice.css";

const STORAGE_KEY = "customerPOs";
const CREATE_PATH = "/dashboard/customer-pos/new";
const STATUS_FILTERS = ['All', 'Draft', 'Confirmed'];

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getStatus = (item) => {
  const value = String(item?.status || 'Draft').trim().toLowerCase();
  if (value === 'draft') return 'Draft';
  if (value === 'confirmed' || value === 'accepted') return 'Confirmed';
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Draft';
};

const getNumber = (item) => item?.poNo || `CPO-${String(item?.id || "").slice(-6)}`;

const getCustomer = (item) => item?.customer || "-";

const Status = ({ item }) => {
  const status = getStatus(item);
  const className = status === 'Confirmed' ? 'paid' : status.toLowerCase();
  return (
    <Box className={`invoice-status invoice-status-${className}`}>
      <span />
      {status}
    </Box>
  );
};

const readItems = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

export default function CustomerPOList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    try { setItems(readItems()); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const totalValue = items.reduce((sum, item) => sum + Number(item.total ?? 0), 0);
    return { count: items.length, value: totalValue };
  }, [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...items]
      .filter((item) => {
        const searchable = [getNumber(item), getCustomer(item)].filter(Boolean).join(' ').toLowerCase();
        const matchesSearch = !term || searchable.includes(term);
        const matchesStatus = filter === 'All' || getStatus(item) === filter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => String(b.poDate || '').localeCompare(String(a.poDate || '')));
  }, [items, search, filter]);

  const removeItem = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const next = items.filter((item) => item.id !== deleteTarget.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setItems(next);
      setDeleteTarget(null);
      setSelected(null);
      toast.success('Customer PO deleted successfully.');
    } catch (error) {
      toast.error('Unable to delete Customer PO.');
    } finally { setDeleting(false); }
  };

  const editItem = (item) => {
    navigate(CREATE_PATH, { state: { editData: item } });
  };

  return (
    <Box className="invoice-page invoice-list-page">
      <div className="invoice-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />
      <Box className="invoice-shell">
        <Box className="invoice-page-header">
          <Box className="invoice-page-header-left invoice-header-title-group">
            <Box className="invoice-page-icon"><AssignmentOutlined /></Box>
            <Box className="invoice-page-title-wrap">
              <Typography className="invoice-page-title">Customer POs</Typography>
              <Typography className="invoice-page-subtitle">Track customer purchase orders.</Typography>
            </Box>
          </Box>
          <Button className="invoice-header-create-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>
            Record PO
          </Button>
        </Box>

        <Box className="invoice-list-summary">
          <Card className="invoice-summary-card invoice-summary-card-total">
            <Box className="invoice-summary-icon"><AssignmentOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total POs</span>
              <strong>{stats.count}</strong>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-value">
            <Box className="invoice-summary-icon"><CurrencyRupeeOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total value</span>
              <strong>{money(stats.value)}</strong>
            </Box>
          </Card>
        </Box>

        {!filtersOpen ? (
          <button type="button" className="invoice-show-filters-btn" onClick={() => setFiltersOpen(true)}>
            <SearchOutlined className="invoice-show-filters-icon" />
            <span>Show Filters</span>
          </button>
        ) : (
          <Card className="invoice-filter-card">
            <CardContent>
              <Box className="invoice-filter-head">
                <Box className="invoice-filter-head-left">
                  <button type="button" className="invoice-filter-title-btn" onClick={() => setFiltersOpen(false)}>
                    <Box className="invoice-filter-icon"><SearchOutlined /></Box>
                    <Typography className="invoice-filter-title">Filters</Typography>
                    <ExpandLessIcon className="invoice-filter-collapse-icon" />
                  </button>
                </Box>
                <Button className="invoice-filter-clear" onClick={() => { setSearch(''); setFilter('All'); }} disabled={!search && filter === 'All'}>Clear</Button>
              </Box>
              <Stack className="invoice-filter-controls" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField className="invoice-field invoice-search-field" placeholder="Search PO number or customer" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <SearchOutlined sx={{ color: '#94a3b8', mr: 1 }} /> }} />
                <TextField select className="invoice-field invoice-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  {STATUS_FILTERS.map((item) => (<MenuItem key={item} value={item}>{item === 'All' ? 'All statuses' : item}</MenuItem>))}
                </TextField>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card className="invoice-list-card">
          {loading ? (
            <Box className="invoice-loading"><CircularProgress size={28} /><Typography>Loading Customer POs...</Typography></Box>
          ) : filtered.length === 0 ? (
            <Box className="invoice-empty">
              <Box className="invoice-empty-icon"><AssignmentOutlined /></Box>
              <Typography>No customer POs found</Typography>
              <Button className="invoice-primary-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>Record PO</Button>
            </Box>
          ) : (
            <Box className="invoice-table-container">
              <Table className="invoice-professional-table">
                <TableHead>
                  <TableRow>
                    <TableCell>PO #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id || getNumber(item)} hover>
                      <TableCell><button type="button" className="invoice-number-link" onClick={() => setSelected(item)}>{getNumber(item)}</button></TableCell>
                      <TableCell>{getCustomer(item)}</TableCell>
                      <TableCell>{formatDate(item.poDate)}</TableCell>
                      <TableCell><Status item={item} /></TableCell>
                      <TableCell align="right"><Typography className="invoice-amount">{money(item.total)}</Typography></TableCell>
                      <TableCell align="center">
                        <Box className="invoice-row-actions">
                          <IconButton className="invoice-row-action invoice-row-view" title="View" onClick={() => setSelected(item)}><VisibilityOutlined /></IconButton>
                          <IconButton className="invoice-row-action invoice-row-edit" title="Edit" onClick={() => editItem(item)}><EditOutlined /></IconButton>
                          <IconButton className="invoice-row-action invoice-row-delete" title="Delete" onClick={() => setDeleteTarget(item)}><DeleteOutlineOutlined /></IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{selected ? `Customer PO ${getNumber(selected)}` : 'Customer PO'}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box flex={1}><Typography variant="caption">Customer</Typography><Typography fontWeight={700}>{getCustomer(selected)}</Typography></Box>
                <Box flex={1}><Typography variant="caption">PO Date</Typography><Typography fontWeight={700}>{formatDate(selected.poDate)}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Total</Typography><Typography fontWeight={800}>{money(selected.total)}</Typography></Box>
              </Stack>
              {selected.sourceRef && (
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Source: {selected.sourceRef.number || 'Quotation'}
                </Typography>
              )}
              <Typography variant="subtitle2">Items</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>#</TableCell><TableCell>Item</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">GST %</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {(selected.items || []).map((row, index) => {
                    const qty = Number(row.qty || 0);
                    const rate = Number(row.rate || 0);
                    const gst = Number(row.gst || 0);
                    const gstAmount = (qty * rate * gst) / 100;
                    return (
                      <TableRow key={row.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.item || '-'}</TableCell>
                        <TableCell align="right">{qty}</TableCell>
                        <TableCell align="right">{money(rate)}</TableCell>
                        <TableCell align="right">{gst}%</TableCell>
                        <TableCell align="right">{money(qty * rate + gstAmount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected && <Button variant="contained" onClick={() => { setSelected(null); editItem(selected); }}>Edit PO</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Customer PO?</DialogTitle>
        <DialogContent><Typography color="text.secondary">This will permanently remove {deleteTarget ? getNumber(deleteTarget) : 'this PO'}.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeItem} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
